import React from "react";
import styled from "styled-components";
import useIdle from "../../hooks/useIdle";
import { setPauseReason } from "../../sound/sound";

// The screensaver: 3D Pipes after a few minutes of inactivity, dismissed
// by any input (the idle hook flips and this unmounts). The pipes module
// (and three.js with it) loads lazily on first activation, so it costs
// the main bundle nothing. Sits under the CRT glass — the monitor is
// still a monitor. Dev knob: ?ss=10 → 10-second idle timeout.
const DEFAULT_IDLE_MS = 3 * 60 * 1000;

// Exported: the idle model is screensaver at Y (this), game timeout at
// X (server idle window), with Y < X. The hook uses this threshold to
// decide whether the player is "awake" (show the no-move warning) or
// gone (screensaver owns the experience; they meet the timed-out notice
// on wake).
export function screensaverIdleMs() {
  if (typeof window === "undefined") return DEFAULT_IDLE_MS;
  const raw = new URLSearchParams(window.location.search).get("ss");
  if (!raw) return DEFAULT_IDLE_MS;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n * 1000 : DEFAULT_IDLE_MS;
}

function PipesCanvas() {
  const canvasRef = React.useRef(null);
  React.useEffect(() => {
    let stop = null;
    let cancelled = false;
    import("./pipes.js").then(({ startPipes }) => {
      if (cancelled || !canvasRef.current) return;
      stop = startPipes(canvasRef.current);
    });
    return () => {
      cancelled = true;
      if (stop) stop();
    };
  }, []);
  return <Canvas ref={canvasRef} />;
}

function Screensaver() {
  const idle = useIdle(screensaverIdleMs());
  // While the pipes are actually running, the machine's speaker rests
  // too: register the screensaver as its own audio pause reason (the
  // hidden-tab reason is tracked independently in sound.js — clearing
  // one can never resume audio while the other still holds).
  React.useEffect(() => {
    if (!idle) return undefined;
    setPauseReason("screensaver", true);
    return () => setPauseReason("screensaver", false);
  }, [idle]);
  if (!idle) return null;
  return (
    <Cover
      // Swallow the waking mousedown so it can't fall through and play a
      // card underneath.
      onMouseDownCapture={(e) => e.stopPropagation()}
    >
      <PipesCanvas />
    </Cover>
  );
}

const Cover = styled.div`
  position: fixed;
  inset: 0;
  z-index: 8500; /* above the desktop, below the CRT glass (9000) */
  background: #000;
  cursor: none;
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
`;

export default Screensaver;
